import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAllTeams,
  filterTeamsByQuery,
  getCachedAllTeams,
} from '../api/authTeams';
import { TeamOption } from '../types/auth/team';

export function useAllTeams() {
  const cached = getCachedAllTeams();
  const [allTeams, setAllTeams] = useState<TeamOption[]>(cached ?? []);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  const loadTeams = useCallback(async () => {
    const existing = getCachedAllTeams();
    if (existing !== null) {
      setAllTeams(existing);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const teams = await fetchAllTeams();
      setAllTeams(teams);
    } catch {
      setAllTeams([]);
      setError('Failed to load colleges. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  const results = useMemo(
    () => filterTeamsByQuery(allTeams, query),
    [allTeams, query]
  );

  const showEmptyResults =
    !loading && !error && query.trim().length >= 1 && results.length === 0;

  return {
    query,
    setQuery,
    results,
    allTeams,
    loading,
    error,
    retry: loadTeams,
    showEmptyResults,
  };
}
